import "@sfs/config/wdyr";
import {persistor, store} from "@app/store";

import "@styles/App.less";
import {OnlineStatusProvider} from "@shared/hooks/useOnlineStatus";
import {useTheme} from "@shared/hooks/useTheme";
import {WindowDimensionsProvider} from "@sfs/hooks/useWindowDimensions";
import {initI18n} from "@shared/services/i18n";
import {initSentry} from "@sfs/services/sentry";
import {OFFLINE_MODE, useInitializeOfflineModeOnStart} from "@shared/utils/offlineModeUtilities";
import {UserTourProvider} from "@shared/utils/userTourUtilities";
import {App, ConfigProvider} from "antd";
import frFR from "antd/es/locale/fr_FR";
import React, {Suspense} from "react";
import {createRoot} from "react-dom/client";
import {useTranslation} from "react-i18next";
import {Provider} from "react-redux";
import {BrowserRouter} from "react-router-dom";
import {PersistGate} from "redux-persist/integration/react";
import NoeApp from "./App";
import {logBuildInfo} from "@sfs/utils/getBuildInfo";
import {PostHog} from "@sfs/config/PostHog";
import {URLS} from "@app/configuration";

initSentry();
initI18n();
logBuildInfo();

const Root: React.FC = () => {
  const {i18n} = useTranslation();
  const theme = useTheme();
  useInitializeOfflineModeOnStart();

  return (
    <Suspense fallback={null}>
      <PostHog noeApiUrl={URLS.API} apiKey={process.env.REACT_APP_POSTHOG_KEY}>
        <BrowserRouter>
          <WindowDimensionsProvider>
            <OnlineStatusProvider>
              <ConfigProvider locale={i18n.language === "fr" ? frFR : undefined} theme={theme}>
                <App>
                  <UserTourProvider>
                    <NoeApp />
                  </UserTourProvider>
                </App>
              </ConfigProvider>
            </OnlineStatusProvider>
          </WindowDimensionsProvider>
        </BrowserRouter>
      </PostHog>
    </Suspense>
  );
};

const container = document.getElementById("noe-app-root") as HTMLElement;
const root = createRoot(container);
root.render(
  <Provider store={store}>
    {OFFLINE_MODE ? (
      <PersistGate loading={null} persistor={persistor}>
        <Root />
      </PersistGate>
    ) : (
      <Root />
    )}
  </Provider>
);
